import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import bookingAPI from '../services/bookingApi';
import { colors } from '../styles/colors';
import { getResponsiveStyles } from '../styles/globalStyles';
import { useBooking } from '../context/BookingContext';
import { useUser } from '../context/UserContext';
import { useResponsive } from '../hooks/useResponsive';

export default function RequirementsPage() {
  const navigate = useNavigate();
  const { selectedHall, selectedTimeSlots, updateForm, bookingForm, selectedDate } = useBooking();
  const { user, isAuthenticated, loading: userLoading } = useUser();
  const { screenSize, isMobile, isTablet, isLaptop, isDesktop, getValueByBreakpoint } = useResponsive();
  const [formData, setFormData] = useState({
    phone: bookingForm.phone || user?.phone || '',
    organization: bookingForm.organization || user?.organization || '',
    eventTitle: bookingForm.eventTitle || '',
    description: bookingForm.description || '',
    attendees: bookingForm.attendees || '',
  });
  const [loading, setLoading] = useState(false);

  // Get responsive styles based on current screen size
  const responsiveStyles = getResponsiveStyles(screenSize.width);

  // Check if user is authenticated, redirect to login if not
  useEffect(() => {
    // Wait for user context to finish loading before checking authentication
    if (!userLoading && !isAuthenticated) {
      alert('Please log in to continue with your booking.');
      navigate('/');
    }
  }, [isAuthenticated, userLoading, navigate]);

  // Update form data when user data becomes available
  useEffect(() => {
    if (user && !userLoading) {
      setFormData(prev => ({
        ...prev,
        phone: prev.phone || user.phone || '',
        organization: prev.organization || user.organization || '',
      }));
    }
  }, [user, userLoading]);

  // Create dynamic styles based on screen size - MUST be defined before any returns
  const styles = {
    container: {
      ...responsiveStyles.container,
    },
    keyboardView: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
    },
    scrollView: {
      flex: 1,
      overflowY: 'auto',
      padding: getValueByBreakpoint({
        mobile: '0',
        tablet: '0 16px',
        laptop: '0 32px'
      }),
    },
    header: {
      padding: getValueByBreakpoint({
        mobile: '16px',
        tablet: '24px',
        laptop: '32px'
      }),
      paddingBottom: getValueByBreakpoint({
        mobile: '12px',
        tablet: '16px',
        laptop: '24px'
      }),
      background: isMobile ? 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)' : colors.background,
      color: isMobile ? colors.white : colors.text,
      borderRadius: isMobile ? '0 0 20px 20px' : 0,
      marginBottom: getValueByBreakpoint({
        mobile: '16px',
        tablet: '20px',
        laptop: '24px'
      }),
    },
    title: {
      fontSize: getValueByBreakpoint({
        mobile: '20px',
        tablet: '26px',
        laptop: '32px'
      }),
      fontWeight: 'bold',
      color: isMobile ? colors.white : colors.text,
      marginBottom: getValueByBreakpoint({
        mobile: '12px',
        tablet: '16px',
        laptop: '20px'
      }),
      textAlign: 'center',
    },
    card: {
      ...responsiveStyles.card,
      margin: getValueByBreakpoint({
        mobile: '0 16px 16px 16px',
        tablet: '0 0 20px 0',
        laptop: '0 0 24px 0'
      }),
    },
    bookingSummary: {
      borderLeft: `4px solid ${colors.primary}`,
      backgroundColor: isMobile ? 'rgba(255,255,255,0.95)' : colors.cardBackground,
      backdropFilter: isMobile ? 'blur(10px)' : 'none',
    },
    summaryRow: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'flex-start' : 'center',
      marginBottom: getValueByBreakpoint({
        mobile: '12px',
        tablet: '8px',
        laptop: '8px'
      }),
      gap: isMobile ? '4px' : '0',
    },
    summaryLabel: {
      fontSize: getValueByBreakpoint({
        mobile: '14px',
        tablet: '15px',
        laptop: '16px'
      }),
      color: colors.textLight,
      fontWeight: '500',
      flex: isMobile ? 'none' : 1,
    },
    summaryValue: {
      fontSize: getValueByBreakpoint({
        mobile: '15px',
        tablet: '16px',
        laptop: '17px'
      }),
      color: colors.text,
      fontWeight: '600',
      flex: isMobile ? 'none' : 2,
      textAlign: isMobile ? 'left' : 'right',
    },
    form: {
      padding: getValueByBreakpoint({
        mobile: '0 16px',
        tablet: '0 24px',
        laptop: '0 32px'
      }),
      maxWidth: getValueByBreakpoint({
        mobile: '100%',
        tablet: '100%',
        laptop: '800px'
      }),
      alignSelf: 'center',
      width: '100%',
      boxSizing: 'border-box',
    },
    userInfoCard: {
      backgroundColor: isMobile ? 'rgba(255,255,255,0.95)' : colors.light,
      padding: getValueByBreakpoint({
        mobile: '16px',
        tablet: '20px',
        laptop: '24px'
      }),
      borderRadius: getValueByBreakpoint({
        mobile: '12px',
        tablet: '16px',
        laptop: '20px'
      }),
      marginBottom: getValueByBreakpoint({
        mobile: '16px',
        tablet: '20px',
        laptop: '24px'
      }),
      border: `1px solid ${colors.border}`,
      backdropFilter: isMobile ? 'blur(10px)' : 'none',
      ...responsiveStyles.shadow,
    },
    userInfoTitle: {
      fontSize: getValueByBreakpoint({
        mobile: '16px',
        tablet: '18px',
        laptop: '20px'
      }),
      fontWeight: '600',
      color: colors.text,
      marginBottom: getValueByBreakpoint({
        mobile: '12px',
        tablet: '14px',
        laptop: '16px'
      }),
      textAlign: isMobile ? 'center' : 'left',
    },
    userInfoRow: {
      display: 'flex',
      justifyContent: isMobile ? 'center' : 'space-between',
      alignItems: 'center',
      marginBottom: getValueByBreakpoint({
        mobile: '8px',
        tablet: '6px',
        laptop: '8px'
      }),
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '4px' : '0',
    },
    userInfoLabel: {
      fontSize: getValueByBreakpoint({
        mobile: '14px',
        tablet: '15px',
        laptop: '16px'
      }),
      color: colors.textLight,
      fontWeight: '500',
    },
    userInfoValue: {
      fontSize: getValueByBreakpoint({
        mobile: '15px',
        tablet: '16px',
        laptop: '17px'
      }),
      color: colors.text,
      fontWeight: '600',
    },
    formRow: {
      display: 'flex',
      flexDirection: isTablet ? 'row' : 'column',
      justifyContent: 'space-between',
      gap: getValueByBreakpoint({
        mobile: '16px',
        tablet: '20px',
        laptop: '24px'
      }),
      marginBottom: getValueByBreakpoint({
        mobile: '16px',
        tablet: '20px',
        laptop: '24px'
      }),
    },
    inputGroup: {
      marginBottom: getValueByBreakpoint({
        mobile: '16px',
        tablet: '20px',
        laptop: '24px'
      }),
      flex: 1,
    },
    inputGroupHalf: {
      flex: 1,
    },
    label: {
      fontSize: getValueByBreakpoint({
        mobile: '14px',
        tablet: '16px',
        laptop: '18px'
      }),
      fontWeight: '600',
      color: colors.text,
      marginBottom: getValueByBreakpoint({
        mobile: '8px',
        tablet: '10px',
        laptop: '12px'
      }),
      display: 'block',
    },
    input: {
      ...responsiveStyles.textInput,
      fontSize: getValueByBreakpoint({
        mobile: '16px',
        tablet: '16px',
        laptop: '17px'
      }),
    },
    textArea: {
      height: getValueByBreakpoint({
        mobile: '80px',
        tablet: '100px',
        laptop: '120px'
      }),
      paddingTop: getValueByBreakpoint({
        mobile: '12px',
        tablet: '14px',
        laptop: '16px'
      }),
      verticalAlign: 'top',
      resize: 'vertical',
      fontFamily: 'inherit',
      lineHeight: 1.4,
    },
    footer: {
      padding: getValueByBreakpoint({
        mobile: '16px',
        tablet: '24px',
        laptop: '32px'
      }),
      paddingTop: getValueByBreakpoint({
        mobile: '16px',
        tablet: '20px',
        laptop: '24px'
      }),
      background: isMobile ? colors.background : 'transparent',
    },
    submitButton: {
      backgroundColor: colors.success,
      color: colors.white,
      border: 'none',
      borderRadius: getValueByBreakpoint({
        mobile: '12px',
        tablet: '14px',
        laptop: '16px'
      }),
      padding: getValueByBreakpoint({
        mobile: '16px 24px',
        tablet: '18px 32px',
        laptop: '20px 40px'
      }),
      fontSize: getValueByBreakpoint({
        mobile: '16px',
        tablet: '18px',
        laptop: '20px'
      }),
      fontWeight: 'bold',
      cursor: 'pointer',
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: getValueByBreakpoint({
        mobile: '52px',
        tablet: '56px',
        laptop: '60px'
      }),
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 8px rgba(39, 174, 96, 0.2)',
      '&:hover': {
        backgroundColor: '#229954',
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 12px rgba(39, 174, 96, 0.3)',
      },
    },
    submitButtonDisabled: {
      backgroundColor: colors.gray,
      opacity: 0.7,
      cursor: 'not-allowed',
      '&:hover': {
        transform: 'none',
        backgroundColor: colors.gray,
      },
    },
    submitButtonText: {
      color: colors.white,
      fontSize: 'inherit',
      fontWeight: 'bold',
    },
    spinner: {
      width: 20,
      height: 20,
      border: '2px solid transparent',
      borderTop: '2px solid white',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      marginRight: '8px',
    },
    loadingText: {
      fontSize: getValueByBreakpoint({
        mobile: '14px',
        tablet: '16px',
        laptop: '18px'
      }),
      color: colors.textLight,
      marginTop: getValueByBreakpoint({
        mobile: '12px',
        tablet: '16px',
        laptop: '20px'
      }),
      textAlign: 'center',
    },
    errorContainer: {
      flex: 1,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column',
      padding: getValueByBreakpoint({
        mobile: '20px',
        tablet: '32px',
        laptop: '40px'
      }),
    },
    errorText: {
      fontSize: getValueByBreakpoint({
        mobile: '16px',
        tablet: '18px',
        laptop: '20px'
      }),
      color: colors.danger,
      textAlign: 'center',
      marginBottom: getValueByBreakpoint({
        mobile: '16px',
        tablet: '20px',
        laptop: '24px'
      }),
    },
    backButton: {
      backgroundColor: colors.primary,
      color: colors.white,
      border: 'none',
      borderRadius: getValueByBreakpoint({
        mobile: '8px',
        tablet: '10px',
        laptop: '12px'
      }),
      padding: getValueByBreakpoint({
        mobile: '12px 20px',
        tablet: '14px 24px',
        laptop: '16px 28px'
      }),
      fontSize: getValueByBreakpoint({
        mobile: '14px',
        tablet: '16px',
        laptop: '18px'
      }),
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: colors.primaryDark,
      },
    },
    backButtonText: {
      color: colors.white,
      fontSize: 'inherit',
      fontWeight: '600',
    },
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    const { eventTitle, attendees } = formData;
    
    if (!eventTitle.trim()) {
      alert('Please enter the event title');
      return false;
    }
    
    if (!attendees.trim() || isNaN(attendees) || parseInt(attendees) <= 0) {
      alert('Please enter a valid number of attendees');
      return false;
    }
    
    if (parseInt(attendees) > selectedHall.capacity) {
      alert(`Maximum capacity for ${selectedHall.name} is ${selectedHall.capacity} people`);
      return false;
    }
    
    return true;
  };

  const handleSubmit = async () => {
    // console.log('=== HANDLE SUBMIT STARTED ===');
    // console.log('Form validation started...');
    
    if (!validateForm()) {
      // console.log('Form validation failed');
      return;
    }
    
    // console.log('Form validation passed, setting loading to true');
    setLoading(true);
    updateForm(formData);
    
    try {
      const bookingData = {
        hall_id: selectedHall.id,
        time_slot_id: selectedTimeSlots[0].id,
        time_slot_ids: selectedTimeSlots.map(slot => slot.id),
        booking_date: selectedDate || new Date().toISOString().split('T')[0],
        requester_name: user?.name,
        email: user?.email,
        phone: formData.phone || user?.phone,
        organization: formData.organization || user?.organization || null,
        event_title: formData.eventTitle,
        purpose: formData.description || null,
        attendees: parseInt(formData.attendees),
        notes: `Event: ${formData.eventTitle}${formData.description ? ` - ${formData.description}` : ''}`,
        status: 'pending',
        user_id: user?.id
      };
      
      // console.log('=== BOOKING SUBMISSION DEBUG ===');
      // console.log('Selected Hall:', selectedHall);
      // console.log('Selected Time Slots:', selectedTimeSlots);
      // console.log('Selected Date:', selectedDate);
      // console.log('Form Data:', formData);
      // console.log('Final Booking Data:', JSON.stringify(bookingData, null, 2));
      // console.log('API URL:', 'http://127.0.0.1:5000/api/bookings');
      // console.log('================================');
      
      const response = await bookingAPI.submitBooking(bookingData);
      
      if (response.success) {
        const confirmationData = {
          name: user?.name,
          email: user?.email,
          phone: formData.phone || user?.phone,
          organization: formData.organization || user?.organization,
          eventTitle: formData.eventTitle,
          description: formData.description,
          attendees: formData.attendees,
          hall: selectedHall,
          timeSlots: selectedTimeSlots,
          timeSlot: selectedTimeSlots.map(slot => slot.time).join(', '),
          booking_id: response.data?.booking_id || response.booking_id,
          bookingId: response.data?.booking_id || response.booking_id,
          booking_date: selectedDate,
          selectedDate: selectedDate,
          status: 'pending',
          submittedAt: new Date().toISOString(),
        };
        
        navigate('/confirmation', { state: { booking: confirmationData } });
        alert('Booking request submitted successfully!');
      } else {
        alert(response.error || 'Failed to submit booking request. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting booking:', error);
      alert('Unable to submit booking request. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking authentication
  if (userLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <div style={styles.spinner}></div>
          <div style={styles.loadingText}>Verifying authentication...</div>
        </div>
      </div>
    );
  }

  if (!selectedHall || !selectedTimeSlots || selectedTimeSlots.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <div style={styles.errorText}>Please select a hall and time slot first</div>
          <button 
            style={styles.backButton} 
            onClick={() => navigate('/home')}
          >
            <span style={styles.backButtonText}>Go Back to Home</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.keyboardView}>
        <div style={styles.scrollView}>
          <div style={styles.header}>
            <div style={styles.title}>Event Requirements</div>
            <div style={{...styles.card, ...styles.bookingSummary}}>
              <div style={styles.summaryRow}>
                <div style={styles.summaryLabel}>Date:</div>
                <div style={styles.summaryValue}>
                  {selectedDate ? (() => {
                    try {
                      return new Date(selectedDate).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      });
                    } catch (error) {
                      console.error('Date formatting error:', error);
                      return selectedDate;
                    }
                  })() : 'Not selected'}
                </div>
              </div>
              <div style={styles.summaryRow}>
                <div style={styles.summaryLabel}>Hall:</div>
                <div style={styles.summaryValue}>{selectedHall.name}</div>
              </div>
              <div style={styles.summaryRow}>
                <div style={styles.summaryLabel}>Time:</div>
                <div style={styles.summaryValue}>
                  {selectedTimeSlots.map(slot => slot.time).join(', ')}
                </div>
              </div>
              <div style={styles.summaryRow}>
                <div style={styles.summaryLabel}>Location:</div>
                <div style={styles.summaryValue}>{selectedHall.location}</div>
              </div>
              <div style={styles.summaryRow}>
                <div style={styles.summaryLabel}>Capacity:</div>
                <div style={styles.summaryValue}>{selectedHall.capacity} people</div>
              </div>
            </div>
          </div>

          <div style={styles.form}>
            {/* Display logged-in user info */}
            <div style={styles.userInfoCard}>
              <div style={styles.userInfoTitle}>Booking for:</div>
              <div style={styles.userInfoRow}>
                <span style={styles.userInfoLabel}>Name:</span>
                <span style={styles.userInfoValue}>{user?.name}</span>
              </div>
              <div style={styles.userInfoRow}>
                <span style={styles.userInfoLabel}>Email:</span>
                <span style={styles.userInfoValue}>{user?.email}</span>
              </div>
            </div>

            {isLaptop || isDesktop ? (
              // Two-column layout for large screens
              <>
                <div style={styles.formRow}>
                  <div style={{...styles.inputGroup, ...styles.inputGroupHalf}}>
                    <div style={styles.label}>Phone Number</div>
                    <input
                      style={styles.input}
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder={user?.phone || "Enter your phone number"}
                    />
                  </div>

                  <div style={{...styles.inputGroup, ...styles.inputGroupHalf}}>
                    <div style={styles.label}>Organization/Department</div>
                    <input
                      style={styles.input}
                      value={formData.organization}
                      onChange={(e) => handleInputChange('organization', e.target.value)}
                      placeholder={user?.organization || "Enter your organization"}
                    />
                  </div>
                </div>

                <div style={styles.inputGroup}>
                  <div style={styles.label}>Event Title *</div>
                  <input
                    style={styles.input}
                    value={formData.eventTitle}
                    onChange={(e) => handleInputChange('eventTitle', e.target.value)}
                    placeholder="Enter event title"
                    autoCapitalize="words"
                  />
                </div>

                <div style={styles.inputGroup}>
                  <div style={styles.label}>Number of Attendees *</div>
                  <input
                    style={styles.input}
                    type="number"
                    value={formData.attendees}
                    onChange={(e) => handleInputChange('attendees', e.target.value)}
                    placeholder={`Max ${selectedHall.capacity} people`}
                  />
                </div>

                <div style={styles.inputGroup}>
                  <div style={styles.label}>Event Requirements</div>
                  <textarea
                    style={{...styles.input, ...styles.textArea}}
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Brief description of your event (optional)"
                    rows={4}
                  />
                </div>
              </>
            ) : (
              // Single column layout for smaller screens
              <>
                <div style={styles.inputGroup}>
                  <div style={styles.label}>Phone Number</div>
                  <input
                    style={styles.input}
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder={user?.phone || "Enter your phone number"}
                  />
                </div>

                <div style={styles.inputGroup}>
                  <div style={styles.label}>Organization/Department</div>
                  <input
                    style={styles.input}
                    value={formData.organization}
                    onChange={(e) => handleInputChange('organization', e.target.value)}
                    placeholder={user?.organization || "Enter your organization"}
                  />
                </div>

                <div style={styles.inputGroup}>
                  <div style={styles.label}>Event Title *</div>
                  <input
                    style={styles.input}
                    value={formData.eventTitle}
                    onChange={(e) => handleInputChange('eventTitle', e.target.value)}
                    placeholder="Enter event title"
                    autoCapitalize="words"
                  />
                </div>

                <div style={styles.inputGroup}>
                  <div style={styles.label}>Number of Attendees *</div>
                  <input
                    style={styles.input}
                    type="number"
                    value={formData.attendees}
                    onChange={(e) => handleInputChange('attendees', e.target.value)}
                    placeholder={`Max ${selectedHall.capacity} people`}
                  />
                </div>

                <div style={styles.inputGroup}>
                  <div style={styles.label}>Event Requirements</div>
                  <textarea
                    style={{...styles.input, ...styles.textArea}}
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Brief description of your event (optional)"
                    rows={4}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        <div style={styles.footer}>
          <button 
            style={{...styles.submitButton, ...(loading && styles.submitButtonDisabled)}}
            onClick={() => {
              // console.log('Submit button pressed!');
              handleSubmit();
            }}
            disabled={loading}
          >
            {loading ? (
              <div style={styles.spinner}></div>
            ) : (
              <span style={styles.submitButtonText}>Submit Booking Request</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

